import React, { useState, useEffect } from 'react';
import { 
  Brain, Sparkles, Search, Zap, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle2, Users, Target, ChevronDown,
  ChevronRight, Clock, Award, Shield, Activity, User,
  Info, ArrowRight, History, Calendar, Check, X, Flame,
  ListTodo, AlertCircle
} from 'lucide-react';
import { DESIGN } from '../components/DesignSystem';
import { COGNITIVE_LANES, COGNITIVE_LEVELS, teamMembers, tasks } from '../mock';

// ================================================
// MATCH ENGINE COLORS
// ================================================
const MATCH_COLORS = {
  primary: '#00C9A7',
  primaryDark: '#00A88A',
  primaryGlow: 'rgba(0, 201, 167, 0.25)',
  primarySubtle: 'rgba(0, 201, 167, 0.08)',
  surfaceDark: '#0F172A',
  cardBg: '#FFFFFF',
  amber: '#F59E0B',
  amberSubtle: 'rgba(245, 158, 11, 0.1)',
  red: '#EF4444',
  green: '#10B981',
  blue: '#3B82F6',
  grey: '#6B7280',
  greySubtle: 'rgba(107, 114, 128, 0.1)'
};

// ================================================
// MOCK TEAM DATA WITH LOAD, BURNOUT & WORKLOAD CONTEXT
// ================================================
const TEAM_DATA = [
  {
    id: 'tm1',
    name: 'Sarah Chen',
    title: 'Head of Product',
    avatar: 'SC',
    primaryLane: 'strategy',
    level: 'L4',
    currentLoad: 6,
    maxLoad: 8,
    burnoutRisk: 'low',
    laneProficiency: { vision: 'L4', strategy: 'L4', creative: 'L3', operations: 'L2' },
    recentPerformance: 0.92,
    // NEW: Workload context
    activeTasks: 6,
    overdueTasks: 0,
    topCurrentTask: 'Q3 Product Roadmap Review',
    loadTrend: [52, 55, 58, 62, 65, 68, 72, 75] // 8-week trend
  },
  {
    id: 'tm2',
    name: 'Marcus Rivera',
    title: 'Engineering Lead',
    avatar: 'MR',
    primaryLane: 'operations',
    level: 'L4',
    currentLoad: 4,
    maxLoad: 10,
    burnoutRisk: 'low',
    laneProficiency: { strategy: 'L3', execution: 'L4', operations: 'L4', finance: 'L2' },
    recentPerformance: 0.95,
    activeTasks: 4,
    overdueTasks: 0,
    topCurrentTask: 'Infrastructure Migration Planning',
    loadTrend: [45, 42, 38, 40, 42, 38, 40, 40]
  },
  {
    id: 'tm3',
    name: 'Emma Thompson',
    title: 'Creative Director',
    avatar: 'ET',
    primaryLane: 'creative',
    level: 'L4',
    currentLoad: 5,
    maxLoad: 6,
    burnoutRisk: 'elevated',
    laneProficiency: { creative: 'L4', marketing: 'L4', vision: 'L3', relationships: 'L3' },
    recentPerformance: 0.88,
    activeTasks: 7,
    overdueTasks: 2,
    topCurrentTask: 'Brand Identity Refresh',
    loadTrend: [60, 65, 70, 75, 82, 85, 88, 83]
  },
  {
    id: 'tm4',
    name: 'David Park',
    title: 'Operations Manager',
    avatar: 'DP',
    primaryLane: 'operations',
    level: 'L3',
    currentLoad: 8,
    maxLoad: 12,
    burnoutRisk: 'low',
    laneProficiency: { operations: 'L4', execution: 'L3', finance: 'L3', culture: 'L2' },
    recentPerformance: 0.91,
    activeTasks: 8,
    overdueTasks: 1,
    topCurrentTask: 'Vendor Contract Renewals',
    loadTrend: [55, 58, 62, 65, 68, 70, 67, 67]
  },
  {
    id: 'tm5',
    name: 'Aisha Patel',
    title: 'Chief Revenue Officer',
    avatar: 'AP',
    primaryLane: 'revenue',
    level: 'L4',
    currentLoad: 7,
    maxLoad: 8,
    burnoutRisk: 'elevated',
    laneProficiency: { revenue: 'L4', relationships: 'L4', strategy: 'L3', marketing: 'L3' },
    recentPerformance: 0.94,
    activeTasks: 9,
    overdueTasks: 0,
    topCurrentTask: 'Enterprise Pipeline Review',
    loadTrend: [70, 72, 78, 82, 85, 88, 90, 88]
  },
  {
    id: 'tm6',
    name: 'James Liu',
    title: 'VP of Finance',
    avatar: 'JL',
    primaryLane: 'finance',
    level: 'L4',
    currentLoad: 5,
    maxLoad: 7,
    burnoutRisk: 'low',
    laneProficiency: { finance: 'L4', strategy: 'L4', operations: 'L3', vision: 'L2' },
    recentPerformance: 0.89,
    activeTasks: 5,
    overdueTasks: 0,
    topCurrentTask: 'Q2 Budget Finalization',
    loadTrend: [60, 62, 58, 55, 52, 50, 55, 71]
  },
  {
    id: 'tm7',
    name: 'Lisa Chen',
    title: 'Marketing Manager',
    avatar: 'LC',
    primaryLane: 'marketing',
    level: 'L3',
    currentLoad: 3,
    maxLoad: 8,
    burnoutRisk: 'low',
    laneProficiency: { marketing: 'L4', creative: 'L3', relationships: 'L3', revenue: 'L2' },
    recentPerformance: 0.90,
    activeTasks: 3,
    overdueTasks: 0,
    topCurrentTask: 'Social Media Calendar Q2',
    loadTrend: [40, 45, 42, 38, 35, 32, 35, 38]
  },
  {
    id: 'tm8',
    name: 'Ryan Foster',
    title: 'Execution Specialist',
    avatar: 'RF',
    primaryLane: 'execution',
    level: 'L2',
    currentLoad: 9,
    maxLoad: 10,
    burnoutRisk: 'high',
    laneProficiency: { execution: 'L3', operations: 'L2', culture: 'L2' },
    recentPerformance: 0.85,
    activeTasks: 11,
    overdueTasks: 3,
    topCurrentTask: 'Sprint Backlog Cleanup',
    loadTrend: [75, 78, 82, 88, 90, 92, 94, 90]
  }
];

// Mock match history with lane data
const MATCH_HISTORY = [
  { id: 'mh1', date: '2026-02-19', taskName: 'Q2 Marketing Campaign Strategy', assignee: 'Lisa Chen', matchScore: 94, outcome: 'completed', daysToComplete: 12, lane: 'marketing' },
  { id: 'mh2', date: '2026-02-18', taskName: 'Infrastructure Cost Analysis', assignee: 'James Liu', matchScore: 91, outcome: 'completed', daysToComplete: 8, lane: 'finance' },
  { id: 'mh3', date: '2026-02-17', taskName: 'Sales Pipeline Optimization', assignee: 'Aisha Patel', matchScore: 96, outcome: 'in-progress', daysToComplete: null, lane: 'revenue' },
  { id: 'mh4', date: '2026-02-15', taskName: 'Component Library Design System', assignee: 'Emma Thompson', matchScore: 89, outcome: 'completed', daysToComplete: 15, lane: 'creative' },
  { id: 'mh5', date: '2026-02-14', taskName: 'DevOps Process Improvement', assignee: 'Marcus Rivera', matchScore: 97, outcome: 'completed', daysToComplete: 6, lane: 'operations' },
  { id: 'mh6', date: '2026-02-12', taskName: 'Customer Success Workflow', assignee: 'David Park', matchScore: 82, outcome: 'delayed', daysToComplete: 22, lane: 'operations' }
];

// ================================================
// MATCHING ALGORITHM
// ================================================
const calculateMatchScore = (member, requiredLane, requiredLevel, workDescription) => {
  // Lane Alignment (40%)
  const laneData = COGNITIVE_LANES.find(l => l.id === requiredLane);
  const memberLaneProficiency = member.laneProficiency[requiredLane];
  let laneScore = 0;
  if (memberLaneProficiency) {
    const proficiencyLevel = parseInt(memberLaneProficiency.replace('L', ''));
    laneScore = Math.min(proficiencyLevel / 4, 1) * 100;
  }
  if (member.primaryLane === requiredLane) {
    laneScore = Math.min(laneScore + 20, 100);
  }
  
  // Level Fit (25%)
  const requiredLevelNum = requiredLevel ? parseInt(requiredLevel.replace('L', '')) : 2;
  const memberLevelNum = parseInt(member.level.replace('L', ''));
  let levelScore = 100;
  const levelDiff = Math.abs(memberLevelNum - requiredLevelNum);
  if (levelDiff === 0) levelScore = 100;
  else if (levelDiff === 1) levelScore = 80;
  else if (levelDiff === 2) levelScore = 50;
  else levelScore = 30;
  
  // Current Capacity (25%)
  const loadRatio = member.currentLoad / member.maxLoad;
  const capacityScore = Math.max(0, (1 - loadRatio) * 100);
  
  // Burnout Safety (10%)
  let burnoutScore = 100;
  if (member.burnoutRisk === 'elevated') burnoutScore = 60;
  else if (member.burnoutRisk === 'high') burnoutScore = 20;
  
  // Weighted total
  const totalScore = Math.round(
    (laneScore * 0.40) +
    (levelScore * 0.25) +
    (capacityScore * 0.25) +
    (burnoutScore * 0.10)
  );
  
  return {
    total: totalScore,
    laneAlignment: Math.round(laneScore),
    levelFit: Math.round(levelScore),
    capacity: Math.round(capacityScore),
    burnoutSafety: Math.round(burnoutScore)
  };
};

// Generate AI explanation
const generateExplanation = (member, scores, requiredLane, requiredLevel) => {
  const lane = COGNITIVE_LANES.find(l => l.id === requiredLane);
  const laneName = lane?.name || requiredLane;
  
  const parts = [];
  
  // Strong lane match
  if (scores.laneAlignment >= 80) {
    if (member.primaryLane === requiredLane) {
      parts.push(`${member.name.split(' ')[0]}'s primary lane is ${laneName}, making them naturally suited for this work`);
    } else {
      parts.push(`${member.name.split(' ')[0]} has strong proficiency in ${laneName}`);
    }
  } else if (scores.laneAlignment >= 60) {
    parts.push(`${member.name.split(' ')[0]} has developing skills in ${laneName}`);
  }
  
  // Level match
  if (scores.levelFit >= 90) {
    parts.push(`operating at ${member.level} which matches the complexity required`);
  } else if (scores.levelFit >= 70) {
    parts.push(`their ${member.level} experience is a reasonable fit for this work`);
  }
  
  // Capacity
  if (scores.capacity >= 70) {
    parts.push(`and has excellent availability with only ${Math.round((member.currentLoad / member.maxLoad) * 100)}% of their capacity used`);
  } else if (scores.capacity >= 40) {
    parts.push(`with moderate capacity available`);
  } else {
    parts.push(`though their current workload is high`);
  }
  
  // Burnout warning
  if (member.burnoutRisk === 'elevated') {
    parts.push(`. Note: currently at elevated cognitive load`);
  } else if (member.burnoutRisk === 'high') {
    parts.push(`. Warning: high burnout risk - consider timeline flexibility`);
  }
  
  return parts.join(', ').replace(/, \./g, '.') + '.';
};

// ================================================
// 8-POINT SPARKLINE COMPONENT
// ================================================
const Sparkline = ({ data, width = 64, height = 20, color = MATCH_COLORS.primary }) => {
  if (!data || data.length < 2) return null;
  
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Glow dot at the end */}
      <circle
        cx={(data.length - 1) / (data.length - 1) * width}
        cy={height - ((data[data.length - 1] - min) / range) * height}
        r="2.5"
        fill={color}
        style={{ filter: `drop-shadow(0 0 3px ${color})` }}
      />
    </svg>
  );
};

// ================================================
// CIRCULAR SCORE GAUGE (Enhanced for Best Match)
// ================================================
const ScoreGauge = ({ score, size = 100, strokeWidth = 8, isBestMatch = false }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  
  const getColor = () => {
    if (score >= 80) return MATCH_COLORS.primary;
    if (score >= 60) return MATCH_COLORS.amber;
    return MATCH_COLORS.red;
  };
  
  const actualStrokeWidth = isBestMatch ? strokeWidth + 2 : strokeWidth;
  const actualRadius = (size - actualStrokeWidth) / 2;
  const actualCircumference = actualRadius * 2 * Math.PI;
  const actualOffset = actualCircumference - (score / 100) * actualCircumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={actualRadius}
          fill="none"
          stroke="rgba(0,0,0,0.08)"
          strokeWidth={actualStrokeWidth}
        />
        {/* Score circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={actualRadius}
          fill="none"
          stroke={getColor()}
          strokeWidth={actualStrokeWidth}
          strokeLinecap="round"
          strokeDasharray={actualCircumference}
          strokeDashoffset={actualOffset}
          style={{ 
            transition: 'stroke-dashoffset 0.8s ease-out',
            filter: isBestMatch 
              ? `drop-shadow(0 0 8px ${getColor()}70) drop-shadow(0 0 16px ${getColor()}40)`
              : `drop-shadow(0 0 6px ${getColor()}50)`
          }}
        />
        {/* Glow at leading edge for best match */}
        {isBestMatch && (
          <circle
            cx={size / 2 + actualRadius * Math.cos((score / 100) * 2 * Math.PI - Math.PI / 2)}
            cy={size / 2 + actualRadius * Math.sin((score / 100) * 2 * Math.PI - Math.PI / 2)}
            r="4"
            fill={getColor()}
            style={{ filter: `drop-shadow(0 0 6px ${getColor()})` }}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span 
          className={`font-black ${isBestMatch ? 'text-3xl' : 'text-2xl'}`}
          style={{ color: getColor() }}
        >
          {score}%
        </span>
      </div>
    </div>
  );
};

// ================================================
// SUB-FACTOR BAR
// ================================================
const SubFactorBar = ({ label, score, weight, color }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs font-medium w-24 truncate" style={{ color: DESIGN.colors.textSecondary }}>
      {label} ({weight}%)
    </span>
    <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
      <div 
        className="h-full rounded-full transition-all duration-500"
        style={{ 
          width: `${score}%`,
          backgroundColor: color || MATCH_COLORS.primary
        }}
      />
    </div>
    <span className="text-xs font-bold w-8 text-right" style={{ color: DESIGN.colors.textPrimary }}>
      {score}
    </span>
  </div>
);

// ================================================
// CURRENT WORKLOAD SECTION
// ================================================
const CurrentWorkloadSection = ({ member }) => {
  return (
    <div 
      className="p-3 rounded-xl mb-3"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
      data-testid={`workload-section-${member.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: DESIGN.colors.textMuted }}>
          Current Workload
        </span>
        <div className="flex items-center gap-2">
          {/* Active Tasks Badge */}
          <span 
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: MATCH_COLORS.primarySubtle, color: MATCH_COLORS.primary }}
          >
            <ListTodo className="w-3 h-3" />
            {member.activeTasks} active
          </span>
          {/* Overdue Tasks Badge */}
          {member.overdueTasks > 0 && (
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
              style={{ 
                backgroundColor: member.overdueTasks >= 3 ? 'rgba(239, 68, 68, 0.1)' : MATCH_COLORS.amberSubtle,
                color: member.overdueTasks >= 3 ? MATCH_COLORS.red : MATCH_COLORS.amber
              }}
            >
              <AlertCircle className="w-3 h-3" />
              {member.overdueTasks} overdue
            </span>
          )}
        </div>
      </div>
      
      <div className="flex items-center justify-between">
        {/* Top Current Task */}
        <p className="text-xs italic flex-1 truncate pr-4" style={{ color: DESIGN.colors.textSecondary }}>
          Currently: {member.topCurrentTask}
        </p>
        
        {/* 8-Week Load Trend Sparkline */}
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>8w trend:</span>
          <Sparkline 
            data={member.loadTrend} 
            width={56} 
            height={16}
            color={member.burnoutRisk === 'high' ? MATCH_COLORS.red : 
                   member.burnoutRisk === 'elevated' ? MATCH_COLORS.amber : MATCH_COLORS.primary}
          />
        </div>
      </div>
    </div>
  );
};

// ================================================
// MATCH RESULT CARD (Enhanced)
// ================================================
const MatchResultCard = ({ member, scores, rank, requiredLane, requiredLevel, onAssign }) => {
  const lane = COGNITIVE_LANES.find(l => l.id === member.primaryLane);
  const requiredLaneData = COGNITIVE_LANES.find(l => l.id === requiredLane);
  
  const getBadge = () => {
    if (rank === 1) return { label: 'Best Match', color: '#1A1A1A', bg: MATCH_COLORS.primary, isSolid: true };
    if (rank === 2) return { label: 'Strong Match', color: MATCH_COLORS.blue, bg: 'rgba(59, 130, 246, 0.1)' };
    if (rank === 3) return { label: 'Good Match', color: MATCH_COLORS.green, bg: 'rgba(16, 185, 129, 0.1)' };
    if (scores.total < 60) return { label: 'Partial Match', color: MATCH_COLORS.amber, bg: MATCH_COLORS.amberSubtle };
    return null;
  };
  
  const badge = getBadge();
  const explanation = generateExplanation(member, scores, requiredLane, requiredLevel);
  const isPartialMatch = scores.total < 60;
  const isBestMatch = rank === 1;
  
  // Button styles based on match quality
  const getButtonStyle = () => {
    if (isBestMatch) {
      return {
        backgroundColor: MATCH_COLORS.primary,
        color: '#1A1A1A',
        border: 'none',
        boxShadow: `0 4px 15px ${MATCH_COLORS.primaryGlow}`
      };
    }
    if (rank === 2) {
      return {
        backgroundColor: 'transparent',
        color: MATCH_COLORS.primary,
        border: `2px solid ${MATCH_COLORS.primary}`,
        boxShadow: 'none'
      };
    }
    if (rank === 3) {
      return {
        backgroundColor: 'transparent',
        color: MATCH_COLORS.grey,
        border: `2px solid ${MATCH_COLORS.grey}`,
        boxShadow: 'none'
      };
    }
    // Partial match
    return {
      backgroundColor: 'transparent',
      color: MATCH_COLORS.grey,
      border: `2px solid rgba(107, 114, 128, 0.5)`,
      boxShadow: 'none'
    };
  };
  
  const buttonStyle = getButtonStyle();
  
  return (
    <div 
      className={`p-5 rounded-2xl transition-all duration-300 hover:shadow-lg hover:-translate-y-1`}
      style={{ 
        backgroundColor: MATCH_COLORS.cardBg,
        boxShadow: isBestMatch 
          ? `0 8px 32px ${MATCH_COLORS.primaryGlow}, 0 0 0 2px rgba(0, 201, 167, 0.3)`
          : DESIGN.shadows.md,
        borderLeft: isPartialMatch ? `4px solid ${MATCH_COLORS.amber}` : 'none'
      }}
      data-testid={`match-result-${member.id}`}
    >
      <div className="flex gap-5">
        {/* Score Gauge */}
        <div className="flex-shrink-0">
          <ScoreGauge score={scores.total} size={90} strokeWidth={7} isBestMatch={isBestMatch} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header with badge */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              {/* Avatar */}
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold text-white"
                style={{ 
                  background: `linear-gradient(135deg, ${lane?.color || '#64748B'} 0%, ${lane?.color || '#64748B'}99 100%)`
                }}
              >
                {member.avatar}
              </div>
              <div>
                <h4 className="font-bold text-base" style={{ color: DESIGN.colors.textPrimary }}>
                  {member.name}
                </h4>
                <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
                  {member.title}
                </p>
              </div>
            </div>
            
            {badge && (
              <span 
                className={`px-3 py-1 rounded-full text-xs font-bold ${isBestMatch ? 'shadow-lg' : ''}`}
                style={{ 
                  backgroundColor: badge.bg, 
                  color: badge.color,
                  boxShadow: isBestMatch ? `0 4px 12px ${MATCH_COLORS.primaryGlow}` : 'none'
                }}
              >
                {badge.label}
              </span>
            )}
          </div>
          
          {/* Lane & Level */}
          <div className="flex items-center gap-2 mb-3">
            <span 
              className="px-2 py-1 rounded-md text-xs font-semibold"
              style={{ backgroundColor: `${lane?.color}20`, color: lane?.color }}
            >
              {lane?.name}
            </span>
            <span className="text-xs font-medium px-2 py-1 rounded-md bg-gray-100" style={{ color: DESIGN.colors.textSecondary }}>
              {member.level}
            </span>
            {/* Capacity bar */}
            <div className="flex items-center gap-1 ml-auto">
              <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Load:</span>
              <div className="w-16 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div 
                  className="h-full rounded-full"
                  style={{ 
                    width: `${(member.currentLoad / member.maxLoad) * 100}%`,
                    backgroundColor: member.burnoutRisk === 'high' ? MATCH_COLORS.red : 
                                    member.burnoutRisk === 'elevated' ? MATCH_COLORS.amber : MATCH_COLORS.green
                  }}
                />
              </div>
            </div>
          </div>
          
          {/* Sub-factors */}
          <div className="space-y-1.5 mb-3">
            <SubFactorBar label="Lane Alignment" score={scores.laneAlignment} weight={40} color={requiredLaneData?.color} />
            <SubFactorBar label="Level Fit" score={scores.levelFit} weight={25} color={MATCH_COLORS.blue} />
            <SubFactorBar label="Capacity" score={scores.capacity} weight={25} color={MATCH_COLORS.green} />
            <SubFactorBar label="Burnout Safety" score={scores.burnoutSafety} weight={10} color={member.burnoutRisk === 'low' ? MATCH_COLORS.green : MATCH_COLORS.amber} />
          </div>
          
          {/* NEW: Current Workload Section */}
          <CurrentWorkloadSection member={member} />
          
          {/* AI Explanation */}
          <p className="text-sm leading-relaxed mb-4" style={{ color: DESIGN.colors.textSecondary }}>
            {explanation}
          </p>
          
          {/* Warnings */}
          {member.burnoutRisk === 'elevated' && scores.total >= 80 && (
            <div 
              className="flex items-center gap-2 p-2 rounded-lg mb-3"
              style={{ backgroundColor: MATCH_COLORS.amberSubtle }}
            >
              <AlertTriangle className="w-4 h-4" style={{ color: MATCH_COLORS.amber }} />
              <span className="text-xs font-medium" style={{ color: MATCH_COLORS.amber }}>
                Best cognitive match but currently at elevated load — consider timeline or redistribute first.
              </span>
            </div>
          )}
          
          {/* Partial Match Warning - Enhanced */}
          {isPartialMatch && (
            <div 
              className="flex items-center gap-2 p-3 rounded-lg mb-3"
              style={{ 
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                border: `1px solid ${MATCH_COLORS.amber}`
              }}
            >
              <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: MATCH_COLORS.amber }} />
              <span className="text-xs font-medium" style={{ color: MATCH_COLORS.amber }}>
                Partial match — consider additional context or support for this assignment.
              </span>
            </div>
          )}
          
          {/* Assign Button - Differentiated by match quality */}
          <button
            onClick={() => onAssign(member)}
            className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
            style={buttonStyle}
            data-testid={`assign-btn-${member.id}`}
          >
            {isPartialMatch && <AlertTriangle className="w-4 h-4" />}
            Assign to {member.name.split(' ')[0]}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================================================
// MATCH SUMMARY PANEL (Left Panel after search)
// ================================================
const MatchSummaryPanel = ({ results, selectedLane, selectedLevel }) => {
  const laneData = COGNITIVE_LANES.find(l => l.id === selectedLane);
  const levelData = COGNITIVE_LEVELS.find(l => l.id === selectedLevel);
  
  const strongMatches = results.filter(r => r.scores.total >= 80).length;
  const atRiskMembers = results.filter(r => r.member.burnoutRisk === 'elevated' || r.member.burnoutRisk === 'high').length;
  const topMatches = results.slice(0, 3);
  
  // Generate insight line
  const generateInsight = () => {
    const lowLoadStrongMatches = topMatches.filter(r => 
      r.scores.total >= 80 && 
      r.member.burnoutRisk === 'low' && 
      (r.member.currentLoad / r.member.maxLoad) < 0.7
    );
    
    if (lowLoadStrongMatches.length >= 2) {
      const names = lowLoadStrongMatches.slice(0, 2).map(r => r.member.name.split(' ')[0]);
      return `${strongMatches} team members are a strong cognitive fit. ${atRiskMembers > 0 ? `${atRiskMembers} are currently at elevated load — ` : ''}consider ${names.join(' or ')} for best results.`;
    }
    
    if (topMatches.length > 0) {
      return `${strongMatches} strong matches found. ${topMatches[0].member.name.split(' ')[0]} shows the highest cognitive alignment for this work.`;
    }
    
    return 'Analysis complete. Review the results on the right to find your best match.';
  };
  
  return (
    <div 
      className="mt-6 p-5 rounded-xl"
      style={{ backgroundColor: 'rgba(0, 201, 167, 0.05)', border: `1px solid rgba(0, 201, 167, 0.2)` }}
      data-testid="match-summary-panel"
    >
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5" style={{ color: MATCH_COLORS.primary }} />
        <h3 className="font-bold text-sm" style={{ color: DESIGN.colors.textPrimary }}>
          Match Summary
        </h3>
      </div>
      
      <div className="space-y-3">
        {/* Detected Lane */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium" style={{ color: DESIGN.colors.textSecondary }}>
            Detected Lane:
          </span>
          <span 
            className="px-2 py-1 rounded-md text-xs font-semibold"
            style={{ backgroundColor: `${laneData?.color || '#64748B'}20`, color: laneData?.color || '#64748B' }}
          >
            {laneData?.name || 'Auto-detected'}
          </span>
        </div>
        
        {/* Detected Level */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium" style={{ color: DESIGN.colors.textSecondary }}>
            Detected Level:
          </span>
          <span className="text-xs font-semibold px-2 py-1 rounded-md bg-gray-100" style={{ color: DESIGN.colors.textSecondary }}>
            {levelData?.name || 'L2-L3'}
          </span>
        </div>
        
        {/* Strong Matches */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium" style={{ color: DESIGN.colors.textSecondary }}>
            Strong Matches:
          </span>
          <span 
            className="text-xs font-bold px-2 py-1 rounded-full"
            style={{ backgroundColor: MATCH_COLORS.primarySubtle, color: MATCH_COLORS.primary }}
          >
            {strongMatches}
          </span>
        </div>
        
        {/* At-Risk Members */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium" style={{ color: DESIGN.colors.textSecondary }}>
            At-Risk for Assignment:
          </span>
          <span 
            className="text-xs font-bold px-2 py-1 rounded-full"
            style={{ 
              backgroundColor: atRiskMembers > 0 ? MATCH_COLORS.amberSubtle : 'rgba(16, 185, 129, 0.1)',
              color: atRiskMembers > 0 ? MATCH_COLORS.amber : MATCH_COLORS.green
            }}
          >
            {atRiskMembers}
          </span>
        </div>
        
        {/* Divider */}
        <div className="h-px bg-gray-200 my-3" />
        
        {/* Insight Line */}
        <div 
          className="p-3 rounded-lg"
          style={{ backgroundColor: 'rgba(0, 201, 167, 0.08)' }}
        >
          <p className="text-xs leading-relaxed" style={{ color: DESIGN.colors.textSecondary }}>
            <Sparkles className="w-3 h-3 inline mr-1" style={{ color: MATCH_COLORS.primary }} />
            {generateInsight()}
          </p>
        </div>
      </div>
    </div>
  );
};

// ================================================
// AGGREGATE STATS BAR
// ================================================
const AggregateStatsBar = () => {
  // Calculate aggregate stats from history
  const completedMatches = MATCH_HISTORY.filter(m => m.outcome === 'completed');
  const avgScore = Math.round(MATCH_HISTORY.reduce((acc, m) => acc + m.matchScore, 0) / MATCH_HISTORY.length);
  const onTimeRate = Math.round((completedMatches.filter(m => m.daysToComplete <= 15).length / completedMatches.length) * 100);
  const totalMatches = MATCH_HISTORY.length;
  
  return (
    <div className="grid grid-cols-3 gap-4 mb-6" data-testid="aggregate-stats-bar">
      {/* Average Match Score */}
      <div 
        className="p-4 rounded-xl text-center"
        style={{ backgroundColor: MATCH_COLORS.cardBg, boxShadow: DESIGN.shadows.md }}
      >
        <div className="text-3xl font-black" style={{ color: MATCH_COLORS.primary }}>
          {avgScore}%
        </div>
        <div className="text-xs font-medium mt-1" style={{ color: DESIGN.colors.textSecondary }}>
          Average Match Score
        </div>
      </div>
      
      {/* On-Time Completion Rate */}
      <div 
        className="p-4 rounded-xl text-center"
        style={{ backgroundColor: MATCH_COLORS.cardBg, boxShadow: DESIGN.shadows.md }}
      >
        <div className="text-3xl font-black" style={{ color: MATCH_COLORS.green }}>
          {onTimeRate}%
        </div>
        <div className="text-xs font-medium mt-1" style={{ color: DESIGN.colors.textSecondary }}>
          On-Time Completion
        </div>
      </div>
      
      {/* Total Matches */}
      <div 
        className="p-4 rounded-xl text-center"
        style={{ backgroundColor: MATCH_COLORS.cardBg, boxShadow: DESIGN.shadows.md }}
      >
        <div className="text-3xl font-black" style={{ color: DESIGN.colors.textPrimary }}>
          {totalMatches}
        </div>
        <div className="text-xs font-medium mt-1" style={{ color: DESIGN.colors.textSecondary }}>
          Total Matches Made
        </div>
      </div>
    </div>
  );
};

// ================================================
// ALGORITHM EXPLANATION
// ================================================
const AlgorithmExplanation = () => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div 
      className="mt-6 rounded-xl overflow-hidden"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-black/5 transition-colors"
        data-testid="algorithm-explainer-toggle"
      >
        <div className="flex items-center gap-2">
          <Info className="w-5 h-5" style={{ color: MATCH_COLORS.primary }} />
          <span className="font-semibold" style={{ color: DESIGN.colors.textPrimary }}>
            Why These Scores?
          </span>
        </div>
        <ChevronDown 
          className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`}
          style={{ color: DESIGN.colors.textMuted }}
        />
      </button>
      
      {expanded && (
        <div className="px-5 pb-5 space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: DESIGN.colors.textSecondary }}>
            The Cognitive Match Engine uses a weighted algorithm that considers four key factors to find the best person for any piece of work:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-white">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4" style={{ color: MATCH_COLORS.primary }} />
                <span className="font-bold text-sm">Lane Alignment (40%)</span>
              </div>
              <p className="text-xs" style={{ color: DESIGN.colors.textSecondary }}>
                How well does their cognitive lane — their natural way of thinking — match the type of work? Primary lane matches score highest.
              </p>
            </div>
            
            <div className="p-4 rounded-xl bg-white">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4" style={{ color: MATCH_COLORS.blue }} />
                <span className="font-bold text-sm">Level Fit (25%)</span>
              </div>
              <p className="text-xs" style={{ color: DESIGN.colors.textSecondary }}>
                Does their engagement level (L1-L4) match the complexity? L4 thinkers on L1 tasks may disengage; L1 on L4 may struggle.
              </p>
            </div>
            
            <div className="p-4 rounded-xl bg-white">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4" style={{ color: MATCH_COLORS.green }} />
                <span className="font-bold text-sm">Current Capacity (25%)</span>
              </div>
              <p className="text-xs" style={{ color: DESIGN.colors.textSecondary }}>
                What percentage of their workload is available? People with more capacity can take on new work without overload.
              </p>
            </div>
            
            <div className="p-4 rounded-xl bg-white">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4" style={{ color: MATCH_COLORS.amber }} />
                <span className="font-bold text-sm">Burnout Safety (10%)</span>
              </div>
              <p className="text-xs" style={{ color: DESIGN.colors.textSecondary }}>
                Is this person at risk of burnout? Even a perfect cognitive match shouldn't be assigned work if they're at capacity.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ================================================
// MATCH HISTORY SECTION (Enhanced)
// ================================================
const MatchHistorySection = () => {
  return (
    <div 
      className="mt-8 p-6 rounded-2xl"
      style={{ backgroundColor: MATCH_COLORS.cardBg, boxShadow: DESIGN.shadows.lg }}
      data-testid="match-history-section"
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: MATCH_COLORS.primarySubtle }}
        >
          <History className="w-5 h-5" style={{ color: MATCH_COLORS.primary }} />
        </div>
        <div>
          <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>
            Match Intelligence History
          </h3>
          <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
            Track outcomes to improve future matching
          </p>
        </div>
      </div>
      
      {/* Aggregate Stats Bar */}
      <AggregateStatsBar />
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b" style={{ borderColor: DESIGN.colors.border }}>
              <th className="text-left py-3 px-2 text-xs font-semibold" style={{ color: DESIGN.colors.textSecondary }}>Date</th>
              <th className="text-left py-3 px-2 text-xs font-semibold" style={{ color: DESIGN.colors.textSecondary }}>Task</th>
              <th className="text-left py-3 px-2 text-xs font-semibold" style={{ color: DESIGN.colors.textSecondary }}>Assigned To</th>
              <th className="text-center py-3 px-2 text-xs font-semibold" style={{ color: DESIGN.colors.textSecondary }}>Match Score</th>
              <th className="text-center py-3 px-2 text-xs font-semibold" style={{ color: DESIGN.colors.textSecondary }}>30-Day Outcome</th>
            </tr>
          </thead>
          <tbody>
            {MATCH_HISTORY.map(record => {
              const laneData = COGNITIVE_LANES.find(l => l.id === record.lane);
              
              return (
                <tr 
                  key={record.id}
                  className="border-b hover:bg-gray-50 transition-colors"
                  style={{ 
                    borderColor: DESIGN.colors.border,
                    borderLeft: `3px solid ${laneData?.color || '#64748B'}`
                  }}
                >
                  <td className="py-3 px-2">
                    <span className="text-sm" style={{ color: DESIGN.colors.textMuted }}>
                      {record.date}
                    </span>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2">
                      {/* Lane color dot */}
                      <div 
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: laneData?.color || '#64748B' }}
                      />
                      <span className="text-sm font-medium" style={{ color: DESIGN.colors.textPrimary }}>
                        {record.taskName}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <span className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
                      {record.assignee}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <span 
                      className="inline-flex items-center justify-center w-12 h-7 rounded-full text-xs font-bold"
                      style={{ 
                        backgroundColor: `${laneData?.color || '#64748B'}15`,
                        color: laneData?.color || '#64748B'
                      }}
                    >
                      {record.matchScore}%
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    {record.outcome === 'completed' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: MATCH_COLORS.green }}>
                        <Check className="w-3 h-3" />
                        Completed ({record.daysToComplete}d)
                      </span>
                    )}
                    {record.outcome === 'in-progress' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: MATCH_COLORS.blue }}>
                        <Activity className="w-3 h-3" />
                        In Progress
                      </span>
                    )}
                    {record.outcome === 'delayed' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: MATCH_COLORS.amberSubtle, color: MATCH_COLORS.amber }}>
                        <Clock className="w-3 h-3" />
                        Delayed ({record.daysToComplete}d)
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ================================================
// MAIN MATCH PAGE
// ================================================
const Match = () => {
  const [workDescription, setWorkDescription] = useState('');
  const [selectedLane, setSelectedLane] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [assignedPerson, setAssignedPerson] = useState(null);

  const handleSearch = () => {
    if (!workDescription.trim()) return;
    
    setIsSearching(true);
    setAssignedPerson(null);
    
    // Simulate search delay
    setTimeout(() => {
      // Calculate scores for all team members
      const scoredMembers = TEAM_DATA.map(member => ({
        member,
        scores: calculateMatchScore(member, selectedLane || 'operations', selectedLevel || 'L2', workDescription)
      }));
      
      // Sort by total score descending
      scoredMembers.sort((a, b) => b.scores.total - a.scores.total);
      
      setResults(scoredMembers);
      setIsSearching(false);
      setHasSearched(true);
    }, 800);
  };

  const handleAssign = (member) => {
    setAssignedPerson(member);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="match-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-3">
          <div 
            className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ 
              background: `linear-gradient(135deg, ${MATCH_COLORS.primary} 0%, ${MATCH_COLORS.primaryDark} 100%)`,
              boxShadow: `0 8px 32px ${MATCH_COLORS.primaryGlow}`
            }}
          >
            <Brain className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-black" style={{ color: DESIGN.colors.textPrimary }}>
              Cognitive Match Engine
            </h1>
            <p className="text-lg" style={{ color: DESIGN.colors.textSecondary }}>
              Find the right mind for the right work.
            </p>
          </div>
        </div>
        
        {/* Framework Attribution */}
        <p 
          className="text-center text-xs tracking-wide mt-4"
          style={{ 
            color: DESIGN.colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            opacity: 0.7
          }}
          data-testid="framework-attribution"
        >
          Powered by the Cognitive Lanes & Levels framework — matching work to how people actually think, not just who has availability.
        </p>
      </div>

      {/* Two Panel Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT PANEL - Work Input */}
        <div 
          className="p-6 rounded-2xl"
          style={{ backgroundColor: MATCH_COLORS.cardBg, boxShadow: DESIGN.shadows.lg }}
        >
          <div className="flex items-center gap-3 mb-5">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: MATCH_COLORS.primarySubtle }}
            >
              <Search className="w-5 h-5" style={{ color: MATCH_COLORS.primary }} />
            </div>
            <h2 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>
              Describe the Work
            </h2>
          </div>
          
          {/* Text Input */}
          <textarea
            value={workDescription}
            onChange={(e) => setWorkDescription(e.target.value)}
            placeholder="Describe the task, project, or objective you need to assign. Be specific about what needs to be done, any deadlines, and the level of complexity involved..."
            className="w-full h-40 p-4 rounded-xl border-2 resize-none focus:outline-none transition-colors"
            style={{ 
              borderColor: workDescription ? MATCH_COLORS.primary : DESIGN.colors.border,
              backgroundColor: DESIGN.colors.background
            }}
            data-testid="work-description-input"
          />
          
          {/* Optional Selectors */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            {/* Lane Selector */}
            <div>
              <label className="text-xs font-semibold block mb-2" style={{ color: DESIGN.colors.textSecondary }}>
                Primary Cognitive Lane Required
              </label>
              <select
                value={selectedLane}
                onChange={(e) => setSelectedLane(e.target.value)}
                className="w-full p-3 rounded-xl border-2 focus:outline-none transition-colors appearance-none cursor-pointer"
                style={{ 
                  borderColor: selectedLane ? MATCH_COLORS.primary : DESIGN.colors.border,
                  backgroundColor: DESIGN.colors.background
                }}
                data-testid="lane-selector"
              >
                <option value="">Auto-detect from description</option>
                {COGNITIVE_LANES.map(lane => (
                  <option key={lane.id} value={lane.id}>
                    {lane.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Level Selector */}
            <div>
              <label className="text-xs font-semibold block mb-2" style={{ color: DESIGN.colors.textSecondary }}>
                Engagement Level Required
              </label>
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="w-full p-3 rounded-xl border-2 focus:outline-none transition-colors appearance-none cursor-pointer"
                style={{ 
                  borderColor: selectedLevel ? MATCH_COLORS.primary : DESIGN.colors.border,
                  backgroundColor: DESIGN.colors.background
                }}
                data-testid="level-selector"
              >
                <option value="">Auto-detect</option>
                {COGNITIVE_LEVELS.map(level => (
                  <option key={level.id} value={level.id}>
                    {level.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Search Button */}
          <button
            onClick={handleSearch}
            disabled={!workDescription.trim() || isSearching}
            className="w-full mt-6 py-4 rounded-xl font-bold text-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ 
              backgroundColor: MATCH_COLORS.primary,
              color: 'white',
              boxShadow: `0 8px 32px ${MATCH_COLORS.primaryGlow}`
            }}
            data-testid="find-match-btn"
          >
            {isSearching ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Analyzing cognitive profiles...
              </>
            ) : (
              <>
                <Zap className="w-5 h-5" />
                Find Best Match
              </>
            )}
          </button>
          
          {/* Lane Color Legend */}
          {selectedLane && !hasSearched && (
            <div className="mt-4 p-3 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
              <div className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: COGNITIVE_LANES.find(l => l.id === selectedLane)?.color }}
                />
                <span className="text-sm font-medium" style={{ color: DESIGN.colors.textSecondary }}>
                  Looking for {COGNITIVE_LANES.find(l => l.id === selectedLane)?.name} thinkers
                </span>
              </div>
            </div>
          )}
          
          {/* Match Summary Panel - Shows after search */}
          {hasSearched && results.length > 0 && (
            <MatchSummaryPanel 
              results={results}
              selectedLane={selectedLane || 'operations'}
              selectedLevel={selectedLevel || 'L2'}
            />
          )}
        </div>

        {/* RIGHT PANEL - Results */}
        <div>
          {!hasSearched ? (
            <div 
              className="h-full flex flex-col items-center justify-center p-8 rounded-2xl"
              style={{ backgroundColor: DESIGN.colors.background, minHeight: 400 }}
            >
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: MATCH_COLORS.primarySubtle }}
              >
                <Users className="w-10 h-10" style={{ color: MATCH_COLORS.primary }} />
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
                Ready to Find Your Match
              </h3>
              <p className="text-center max-w-xs" style={{ color: DESIGN.colors.textSecondary }}>
                Describe the work on the left and click "Find Best Match" to see ranked team members based on cognitive fit.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Assignment Success */}
              {assignedPerson && (
                <div 
                  className="p-4 rounded-xl flex items-center gap-3"
                  style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}
                  data-testid="assignment-success"
                >
                  <CheckCircle2 className="w-6 h-6" style={{ color: MATCH_COLORS.green }} />
                  <div>
                    <p className="font-semibold" style={{ color: MATCH_COLORS.green }}>
                      Assigned to {assignedPerson.name}
                    </p>
                    <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
                      Task has been assigned via Cognitive Match
                    </p>
                  </div>
                </div>
              )}
              
              {/* Results List */}
              {results.map((result, index) => (
                <MatchResultCard
                  key={result.member.id}
                  member={result.member}
                  scores={result.scores}
                  rank={index + 1}
                  requiredLane={selectedLane || 'operations'}
                  requiredLevel={selectedLevel || 'L2'}
                  onAssign={handleAssign}
                />
              ))}
              
              {/* Algorithm Explanation */}
              <AlgorithmExplanation />
            </div>
          )}
        </div>
      </div>
      
      {/* Match History */}
      <MatchHistorySection />
    </div>
  );
};

export default Match;
